/**
 * Custom cart API so the theme may facilitate cart manipulation with an integrated
 * gift manager to handle gift with purchases.
 */
class Cart {
  /**
   * Initializes a new cart with an integrated GiftManager.
   */
  constructor() {
    const promos = [];
    if (window.gwp?.promos) {
      promos.push(...window.gwp.promos);
    }
    if (window.freeSamples?.promos) {
      promos.push(...window.freeSamples.promos);
    }

    this.giftManager = new window.GiftManager(promos);
    this.data = null; // Cart data.
  }

  /**
   * Returns the most commonly used configuration object for use with fetch API requests.
   * @returns {Object} The fetch configuration object for JSON content.
   */
  fetchConfig() {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    };
  }

  /**
   * Retrieves the current state of the cart from Shopify.
   * @returns {Promise<Object>} Resolves with Shopify's JSON response if fetching is successful. On success
   * the custom event 'cart:get' is dispatched with this response data.
   * @throws {Error} Thrown if Shopify responds with an error.
   */
  get() {
    return fetch(`${window.routes.cart_url}`, this.fetchConfig())
      .then((response) => response.json())
      .then((response) => {
        this.data = response;
        document.dispatchEvent(
          new CustomEvent('cart:get', { detail: response }),
        );
        return response;
      })
      .catch((error) => {
        console.error(error);
        // Re-throw the error to allow further handling up the call stack.
        throw error;
      });
  }

  /**
   * Clears all items from the cart by sending a request to Shopify.
   * @param {boolean} silent - If false (default) then no custom events are dispatched.
   * @returns {Promise<Object>} Resolves with Shopify's JSON response if clearing is successful. On success
   * the custom event 'cart:clear' is dispatched with this response data.
   * @throws {Error} Thrown if Shopify responds with an error.
   */
  clear(silent = false) {
    return fetch(`${window.routes.cart_clear_url}`, this.fetchConfig())
      .then((response) => response.json())
      .then((response) => {
        this.data = response;
        if (!silent) {
          document.dispatchEvent(
            new CustomEvent('cart:clear', { detail: response }),
          );
        }
        return response;
      })
      .catch((error) => {
        console.error(error);
        // Re-throw the error to allow further handling up the call stack.
        throw error;
      });
  }

  /**
   * Changes a line item in the cart by sending a request to Shopify.
   * @param {Object} item - The line item that needs to be changed. Its 'id' or 'line' property must be specified.
   * @param {number|string} [item.id] - This item's variant ID (number) or its line item key (string).
   * @param {number} [item.line] - This item's 1-based index position in the cart.
   * @param {boolean} silent - If false (default) then no custom events are dispatched.
   * @returns {Promise<Object>} Resolves with Shopify's JSON response if the change is successful. On success
   * the custom event 'cart:change' is dispatched with this response data.
   * @throws {Error} Throws an error if the `item` object is invalid or if the request failed with Shopify.
   */
  change(item, silent = false) {
    // Validate parameter, conforming to Shopify's Change API method.
    const hasId = typeof item.id === 'string' || typeof item.id === 'number';
    const hasLine = typeof item.line === 'number';

    if (!hasId && !hasLine) {
      // An identifier (the line item key) or line number must always be provided.
      return Promise.reject(
        new Error(
          `Invalid input: 'item' change requires an 'id' that must be a variant ID or line item key.`,
        ),
      );
    }

    // Handle stringification within the promise chain.
    return Promise.resolve(JSON.stringify(item))
      .then((serializedChange) => {
        // Make the actual Shopify API call.
        return fetch(`${window.routes.cart_change_url}`, {
          ...this.fetchConfig(),
          body: serializedChange,
        });
      })
      .then((response) => response.json())
      .then((response) => {
        if (!silent) {
          document.dispatchEvent(
            new CustomEvent('cart:change', { detail: response }),
          );
        }
        return response;
      })
      .catch((error) => {
        console.error(error);
        // Re-throw the error to allow further handling up the call stack.
        throw error;
      });
  }

  /**
   * Updates cart quantities by sending a request to Shopify.
   * @param {Object.<string|number, number>} updates - Maps variant IDs or line item keys to their new quantities.
   * @param {boolean} silent - If false (default) then no custom events are dispatched.
   * @returns {Promise<Object>} Resolves with Shopify's JSON response if the update is successful. On success
   * the custom event 'cart:update' is dispatched with this response data.
   * @throws {Error} Throws an error if the `updates` object is invalid or if the request failed with Shopify.
   */
  update(updates, silent) {
    // Validate parameter, conforming to Shopify's Updates API method.
    if (updates === null || typeof updates !== 'object') {
      return Promise.reject(
        new Error(`Invalid input: 'updates' must be an object.`),
      );
    }

    Object.entries(updates).forEach(([key, value]) => {
      if (typeof key !== 'string' && !Number.isInteger(key)) {
        // Key must reference a variant ID or a line item key.
        return Promise.reject(
          new Error(`Invalid updates key: ${key} is not a valid identifier.`),
        );
      }
      if (typeof value !== 'number' || value < 0 || !Number.isInteger(value)) {
        // Value must be a non-negative integer.
        return Promise.reject(
          new Error(
            `Invalid updates value for key ${key}: ${value} is not a valid quantity.`,
          ),
        );
      }
    });

    // Handle stringification within the promise chain.
    return Promise.resolve(JSON.stringify({ updates }))
      .then((serializedUpdates) => {
        // Make the actual Shopify API call.
        return fetch(`${window.routes.cart_update_url}`, {
          ...this.fetchConfig(),
          body: serializedUpdates,
        });
      })
      .then((response) => response.json())
      .then((response) => {
        if (!silent) {
          document.dispatchEvent(
            new CustomEvent('cart:update', { detail: response }),
          );
        }
        return response;
      })
      .catch((error) => {
        console.error(error);
        // Re-throw the error to allow further handling up the call stack.
        throw error;
      });
  }

  /**
   * Sends a request to Shopify to add items to the cart then returns the latest cart state.
   * @param {Object} config - Configuration for the fetch request, including necessary headers.
   * @param {FormData|string} body - The data for this request, can either be a FormData object or stringified JSON.
   * @param {boolean} silent - If false (default) then no custom events are dispatched.
   * @returns {Promise<Object>} Resolves with the response from the server, parsed as JSON. On success the custom
   * event 'cart:add' is dispatched with the response data before get() is returned.
   * @throws {Error} An error is thrown if an item with a selling plan is being added to a cart that already
   * contains a Bundle Builder bundle. Any errors caught while fetching are printed to console then thrown again.
   */
  _add(config, body, silent = false) {
    // A bundle Bundle Builder app and an item with a subscription cannot share the same cart.
    const addingSubscriptionItem =
      body instanceof FormData
        ? body.get('selling_plan')
        : body.includes('selling_plan');
    if (addingSubscriptionItem && this.hasBundleByApp()) {
      throw new Error(window.locales.cart.bundle_error_subscription);
    }

    return fetch(`${window.routes.cart_add_url}`, {
      ...config,
      body,
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        // Send this error message to the mini cart.
        return response.json().then(({ errors }) => {
          throw new Error(errors);
        });
      })
      .then((response) => {
        if (!silent) {
          document.dispatchEvent(
            new CustomEvent('cart:add', { detail: response }),
          );
        }
        return this.get();
      })
      .catch((error) => {
        console.error(error);
        // Display this error in the cart.
        document.dispatchEvent(
          new CustomEvent('mini-cart:display-error', {
            detail: error.message,
          }),
        );
        // Re-throw the error to allow further handling up the call stack.
        throw error;
      });
  }

  /**
   * Adds items to the cart by sending a JSON formatted request To shopify.
   * Calls the private `_add` method to make the actual request.
   * Each item in the 'items' array must strictly conform to the structure as specified by Shopify's API:
   * - id: {number} The variant ID for the item
   * - quantity: {number} The quantity of this item to add.
   * @param {Object[]} items - An array of item objects to be added to the cart.
   * @param {boolean} silent - If false (default) then no custom events are dispatched.
   * @returns {Promise<Object>} If basic input validation fails then the promise is rejected.
   * It then resolves with the response from the server, parsed as JSON.
   * @throws {Error} Thrown if input validation fails or the server responded with an error.
   */
  addItems(items, silent = false) {
    const isDynamicallyPricedBundle = items.find(
      (item) => item.properties && item.properties._rc_bundle_parent,
    );
    if (isDynamicallyPricedBundle && this.hasDynamicallyPricedBundle()) {
      // Only one dynamically priced bundle is allowed per cart.
      document.dispatchEvent(
        new CustomEvent('mini-cart:display-error', {
          detail: window.locales.cart.bundle_error_dynamically_priced,
        }),
      );
      return;
    }

    // Validate parameter. Each item must at least specify its variant ID and quantity.
    if (
      !Array.isArray(items) ||
      items.every(
        (item) =>
          typeof item === 'object' ||
          (item !== null && typeof item.id === 'number') ||
          (Number.isInteger(item.id) && typeof item.quantity === 'number') ||
          Number.isInteger(item.quantity),
      ) === false
    ) {
      return Promise.reject(
        new Error(
          `Invalid input: 'items' must be an array of objects, each object must have its variant ID and quantity specified.`,
        ),
      );
    }
    // Handle stringification within the promise chain.
    return Promise.resolve(JSON.stringify({ items })).then((serializedItems) =>
      this._add(this.fetchConfig(), serializedItems, silent),
    );
  }

  /**
   * Submits form data as a POST request to Shopify. Calls the private `_add` method to make the actual request.
   * @param {HTMLFormElement} form - The product's form element from which data will be serialized.
   * @param {boolean} silent - If false (default) then no custom events are dispatched.
   * @returns {Promise<Object>} If basic input validation fails then the promise is rejected.
   * It then resolves with the response from the server, parsed as JSON.
   * @throws {Error} Thrown if input validation fails or the server responded with an error.
   */
  addForm(form, silent = false) {
    // Reconfigure config to handle XML requests. This is the only cart request that requires XML.
    const config = this.fetchConfig();
    delete config.headers['Content-Type'];
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    // Validate parameter.
    if (!(form instanceof HTMLFormElement)) {
      return Promise.reject(
        new Error(`Invalid input: 'form' must be an HTMLFormElement.`),
      );
    }
    // Wrap FormData creation in a Promise to maintain async pattern.
    return Promise.resolve(new FormData(form)).then((formData) =>
      this._add(config, formData, silent),
    );
  }

  /**
   * Checks if any line item is a bundle by the Bundle Builder app.
   * @returns {boolean} Returns true if a Bundle Builder bundle is found.
   */
  hasBundleByApp() {
    return (
      this.data.items.find(
        (item) => item.properties && item.properties._bundle_id,
      ) !== undefined
    );
  }

  /**
   * Checks if any line item has a selling plan (subscription).
   * @returns {boolean} Returns true if a subscription is found.
   */
  hasSubscription() {
    return (
      this.data.items.find((item) => item.selling_plan_allocation) !== undefined
    );
  }

  /**
   * Checks if any line item is a dynamically priced bundle by Recharge.
   * @returns {boolean} Returns true if a dynamically priced bundle by Recharge is found.
   */
  hasDynamicallyPricedBundle() {
    return this.data.items.find(
      (item) => item.properties && item.properties._rc_bundle_parent,
    );
  }
}

window.cart = new Cart();
